import React, {useMemo, useState} from "react";
import {GameItems} from "../State/GameData";
import {AssemblyRecipe} from "../State/AssemblyRecipes";
import {AssemblyNode, InventoryPulse} from "./AssemblyTypes";
import {L} from "./AssemblyStyles";
import {ItemIcon} from "../SharedComponents/ItemIcon";
import {RecipeSelectionModal} from "./RecipeSelectionModal";

type AssemblyNodeCardProps = {
    node: AssemblyNode;
    ready: boolean;
    recipe?: AssemblyRecipe;
    recipes: AssemblyRecipe[];
    outputPerSecond: number;
    maxOutputPerSecond?: number;
    allocatedOutput?: number;
    totalOutput?: number;
    powerUse: number;
    fulfillment: number;
    status: string;
    powerLimited: boolean;
    powerOffline: boolean;
    inventoryPulses?: InventoryPulse[];
    active?: boolean;
    connectionCount?: number;
    isInputHighlighted?: boolean;
    hasOutputConnection?: boolean;
    missingIngredients?: string[];
    ingredientInputRates?: Array<{itemId: string; quantity: number; availableInput: number; maxInput: number}>;
    onSetRecipe: (nodeId: string, recipeId: string) => void;
    onToggleFlag: (nodeId: string, field: "running" | "overclocked") => void;
    onStartDrag: (event: React.MouseEvent, nodeId: string) => void;
    onDelete: (nodeId: string) => void;
    onStartConnection?: (event: React.MouseEvent, nodeId: string) => void;
    onMouseEnterInput?: (nodeId: string) => void;
    onMouseLeaveInput?: (nodeId: string) => void;
};

const chipColor = (type: AssemblyNode["type"]) => {
    if (type === "source") return "#fde68a";
    if (type === "inventory") return "#a5b4fc";
    return "#bfdbfe";
};

const chipLetter = (node: AssemblyNode) => {
    if (node.type === "source") return (GameItems[node.itemId ?? ""]?.name ?? "?").charAt(0).toUpperCase();
    if (node.type === "inventory") return "I";
    return "A";
};

export function AssemblyNodeCard({
    node,
    ready,
    recipe,
    recipes,
    outputPerSecond,
    maxOutputPerSecond = 0,
    allocatedOutput = 0,
    totalOutput = 0,
    powerUse,
    fulfillment,
    status,
    powerLimited,
    powerOffline,
    inventoryPulses = [],
    active = false,
    connectionCount = 0,
    isInputHighlighted = false,
    hasOutputConnection = false,
    missingIngredients = [],
    ingredientInputRates = [],
    onSetRecipe,
    onToggleFlag,
    onStartDrag,
    onDelete,
    onStartConnection,
    onMouseEnterInput,
    onMouseLeaveInput,
}: AssemblyNodeCardProps) {
    const [showRecipeModal, setShowRecipeModal] = useState(false);

    const throughputLabel = useMemo(() => {
        if (!recipe) return "";
        return `${outputPerSecond.toFixed(2)}/s (max ${maxOutputPerSecond.toFixed(2)}/s)`;
    }, [outputPerSecond, maxOutputPerSecond, recipe]);

    // Calculate ingredient consumption rates
    const ingredientConsumption = useMemo(() => {
        if (!recipe || !node.overclocked === undefined) return [];

        const baseSpeed = node.overclocked ? 2 : 1; // OVERCLOCK_SPEED = 2
        const outputRatio = maxOutputPerSecond > 0 ? outputPerSecond / maxOutputPerSecond : 0;

        return recipe.ingredients.map(ingredient => {
            const baseConsumptionRate = (ingredient.quantity / (recipe.durationMs / 1000)) * baseSpeed;
            const actualConsumptionRate = baseConsumptionRate * outputRatio;
            return {
                ...ingredient,
                consumptionRate: actualConsumptionRate
            };
        });
    }, [recipe, node.overclocked, outputPerSecond, maxOutputPerSecond]);

    // Calculate max power usage
    const maxPowerUse = useMemo(() => {
        if (node.type !== "assembler" || node.running === false) return 0;
        const ASSEMBLER_POWER_USE = 20;
        const OVERCLOCK_POWER = 4;
        return ASSEMBLER_POWER_USE * (node.overclocked ? OVERCLOCK_POWER : 1);
    }, [node.type, node.overclocked, node.running]);

    // Calculate running percentage
    const runningPercentage = useMemo(() => {
        if (!recipe || maxOutputPerSecond === 0) return 0;
        return Math.round((outputPerSecond / maxOutputPerSecond) * 100);
    }, [outputPerSecond, maxOutputPerSecond, recipe]);

    return (
        <L.NodeCard $x={node.position.x} $y={node.position.y} $kind={node.type} $highlight={ready || node.type !== "assembler"} $active={active && node.type === "assembler"}
                    onMouseDown={ev => {
                        ev.preventDefault();
                        onStartDrag(ev, node.id);
                    }}>
            <L.NodeTitle>
                <L.Chip $color={chipColor(node.type)}>
                    {chipLetter(node)}
                </L.Chip>
                <div>{node.label}</div>
                {node.type !== "inventory" && <L.DeleteButton onClick={(e) => {
                    e.stopPropagation();
                    onDelete(node.id);
                }} title="Delete this node">✕</L.DeleteButton>}
            </L.NodeTitle>
            {node.type === "assembler" && recipe && <div>
                {/* Full-width recipe selector button */}
                <L.RecipeSelectButton onClick={(e) => {
                    e.stopPropagation();
                    setShowRecipeModal(true);
                }} aria-label="Select recipe">
                    <ItemIcon itemId={recipe.result.itemId} size={32} />
                    <L.NodeColumn style={{flex: 1}}>
                        <L.NodeLabel>{recipe.name}</L.NodeLabel>
                        <L.SubLabel>{GameItems[recipe.result.itemId].name} x{recipe.result.quantity}</L.SubLabel>
                    </L.NodeColumn>
                    <L.RecipeChangeIcon>⚙</L.RecipeChangeIcon>
                </L.RecipeSelectButton>

                {/* Recipe details */}
                <L.NodeMeta>
                    <div>
                        <strong>Ingredients:</strong>
                        {ingredientInputRates.map((ing) => {
                            const isMissing = missingIngredients.includes(ing.itemId);
                            return (
                                <div key={ing.itemId} style={{marginLeft: '8px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                    <span>{ing.quantity}x {GameItems[ing.itemId].name} - {ing.availableInput.toFixed(2)}/s (max {ing.maxInput.toFixed(2)}/s)</span>
                                    {isMissing && (
                                        <L.IngredientWarning title="Missing ingredient - connect source to input handle">
                                            !
                                        </L.IngredientWarning>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <L.Inline title="Per second throughput"><strong>Throughput:</strong> {throughputLabel}</L.Inline>
                    <L.Inline style={{display: 'flex', alignItems: 'center', gap: '4px'}} title="Current and maximum power usage">
                        <strong>Power:</strong> {powerUse.toFixed(1)} kW / {maxPowerUse.toFixed(1)} kW
                        {powerLimited && (
                            <span style={{color: '#f59e0b', fontSize: '14px'}} title="Low power supply">⚠</span>
                        )}
                    </L.Inline>
                    <L.Inline title="Power usage percentage vs grid availability"><strong>Power supply:</strong> using {Math.round((powerUse / (maxPowerUse || 1)) * 100)}% / {fulfillment}% available</L.Inline>
                </L.NodeMeta>

                {/* Bottom row: Start/Stop button and Overclock toggle */}
                <L.CompactRow style={{marginTop: 8}}>
                    <L.ControlButton $active={node.running !== false}
                                     onClick={(e) => {
                                         e.stopPropagation();
                                         onToggleFlag(node.id, "running");
                                     }}
                                     title={powerOffline ? "No power available" : "Toggle assembly"}>
                        {node.running === false ? "▶ Start" : "⏸ Pause"}
                    </L.ControlButton>
                    <L.ToggleButton $on={node.overclocked}
                                    aria-pressed={node.overclocked}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleFlag(node.id, "overclocked");
                                    }}>
                        ⚡ {node.overclocked ? "Overclock" : "Overclock"}
                    </L.ToggleButton>
                </L.CompactRow>

                {/* Status pills below buttons */}
                {(status.includes("Missing") || status.includes("Power") || status.includes("Output not consumed") || !ready || (ready && status === "Running")) && (
                    <L.CompactRow style={{marginTop: 6}}>
                        {(status.includes("Missing") || status.includes("Power") || status.includes("Output not consumed") || !ready) && (
                            <L.StatusPill $tone={status.includes("Missing") || powerOffline || status.includes("Output not consumed") ? "warn" : status.includes("Power") ? "warn" : "info"}
                                          title={powerLimited ? "Insufficient electricity to run at full capacity" : status.includes("Output not consumed") ? "Connect output to inventory or another assembler" : undefined}>
                                {status}
                            </L.StatusPill>
                        )}
                        {ready && status === "Running" && (
                            <L.StatusPill $tone="ok">Running</L.StatusPill>
                        )}
                    </L.CompactRow>
                )}

                {/* Recipe selection modal */}
                {showRecipeModal && (
                    <RecipeSelectionModal
                        recipes={recipes}
                        selectedRecipeId={node.recipeId}
                        onSelect={(recipeId) => onSetRecipe(node.id, recipeId)}
                        onClose={() => setShowRecipeModal(false)}
                    />
                )}
            </div>}
            {node.type === "source" && <L.NodeMeta>
                <span>
                    Output: {allocatedOutput > 0 ? `${allocatedOutput.toFixed(2)}/s` : '0.00/s'} / {totalOutput > 0 ? `${totalOutput.toFixed(2)}/s` : '0.00/s'}
                </span>
            </L.NodeMeta>}
            {node.type === "inventory" && <L.NodeMeta>
                <span>Drag outputs here to stock the shared inventory.</span>
            </L.NodeMeta>}
            {node.type === "inventory" && inventoryPulses.length > 0 && <L.InventoryPulseStack>
                {inventoryPulses.map(pulse => (
                    <L.InventoryPulse key={pulse.id}>
                        <ItemIcon itemId={pulse.itemId} size={18} />
                        <span>+{pulse.quantity}</span>
                    </L.InventoryPulse>
                ))}
            </L.InventoryPulseStack>}
            {node.type !== "inventory" && onStartConnection && (
                <L.ConnectionHandle
                    $position="right"
                    data-connection-handle="output"
                    $blink={node.type === "assembler" && ready && !hasOutputConnection}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onStartConnection(e, node.id);
                    }}
                    title={node.type === "assembler" && ready && !hasOutputConnection
                        ? "Connect output to inventory or another assembler"
                        : "Drag to create a connection from this node (Output)"}
                />
            )}
            {node.type !== "source" && (
                <L.ConnectionHandle
                    $position="left"
                    data-connection-handle="input"
                    $isHighlighted={isInputHighlighted}
                    $blink={node.type === "assembler" && !ready && !!recipe}
                    onMouseEnter={() => onMouseEnterInput?.(node.id)}
                    onMouseLeave={() => onMouseLeaveInput?.(node.id)}
                    title={node.type === "assembler" && !ready && recipe
                        ? "Missing ingredients - drag ingredient sources here"
                        : "Drop connections here (Input)"}
                />
            )}
        </L.NodeCard>
    );
}
